import React, { useState, useEffect } from "react";
import RecordMedia from "./RecordMedia";
import sendAudio from "./SendAudio";
import InitVirtualInterviewer from "./InitVirtualInterviewer";
import { Button, Grid, Typography } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { Storage } from "aws-amplify";
import {
  getLocalStorage,
  removeItem,
  setLocalStorage,
} from "../../Models/data/useLocalStorage";
import { getParams } from "../../common/util";
import { AnswerText, buttonStyle } from "../../assets/styles/sxStyles";
import { Modal } from "antd";
import { Max_TimeOut, Tech_Error_Message } from "../../common/constant";
import { Keys } from "../../common/keys";

/*
"conversation": [
  {
    "content": "You are an experienced friendly and engaging interviewer with a sense of humor. You goal is to extract as much insight as possible from the panelist being interviewed. The interview is about understanding how people feel about work from home and return to the office. You have an interview flow to respect, and you are free to rephrase the questions as you see fit. We are using this prefix tag to simplify insight extraction later.\n\nINTERVIEW FLOW:\n0. Greating:\n\n1. Overall Experience:\nQuestion: Can you please share your overall experience of working remotely during the COVID-19 pandemic?\n\n2. Productivity and Efficiency:\nQuestion: How would you rate your productivity and efficiency while working remotely compared to when you were working in the office?\n\n3. Work-Life Balance:\nQuestion: Did working remotely improve your work-life balance? If yes, how? If no, why not?\n\n4. Collaboration and Communication:\nQuestion: How did remote work impact your ability to collaborate and communicate with your colleagues and teams? Were there any challenges or benefits?\n\n5. Technology and Infrastructure:\nQuestion: Did you face any technological or infrastructure challenges while working remotely? If yes, please elaborate on the specific issues you encountered.\n\n6. Employee Engagement and Motivation:\nQuestion: How did remote work affect your level of engagement and motivation? Did you feel connected to your team and the organization as a whole?\n\n7. Managerial Support:\nQuestion: How did your managers support you during the remote work period? Did you receive adequate guidance, feedback, and support from your superiors?\n\n8. Training and Development:\nQuestion: Were you able to access necessary training and development opportunities while working remotely? If not, what were the limitations?\n\n9. Mental Health and Well-being:\nQuestion: How did remote work impact your mental health and overall well-being? Did you experience any additional stress or challenges?\n\n10. Preference for Remote or Office Work:\nQuestion: Based on your experience, do you prefer working remotely, working in the office, or a combination of both? Please explain your preference.\n\n11. Recommendations for the Future:\nQuestion: What recommendations or suggestions do you have for the CEO to adjust the post-COVID remote work policy? How can the organization better support remote work or hybrid work models?\n\n12. Lessons Learned:\nQuestion: What valuable lessons did you learn from the remote work experience that can be applied to future work arrangements?\n\n13. Additional Comments:\nQuestion: Is there anything else you would like to share regarding your experience with remote work or any other insights that you believe would be valuable for the CEO to consider?\n\n14. Closing:\n\nYou are the interviewer only. Before deciding to move the the next question on the interview flow, assess if a follow up interaction could enrich insights on current question and dig deeper if appropriate. On first interaction, great the panelist \"Eric\" and explain that you are an AI interviewer, with flows and still in beta. Each time you answer, start with [Question title] follow by what you want to ask. When switching topic within an answer, make sure to add the [Question title] marquee.",
    "role": "system"
   },
   {
    "content": "[Greating]\nHello Eric! I'm an AI interviewer, still in beta, but I promise I won't bite. I'm here to chat with you about your experiences with remote work during the COVID-19 pandemic. I'm sure you've got some interesting insights to share, so let's dive right in, shall we?\n\n[Overall Experience]\nSo, Eric, could you kick things off by giving us a little tour of your journey working from home during the pandemic? How was the overall experience for you?",
    "role": "assistant"
   }
*/

function VideoChat({
  id,
  panelist_,
  firstName_,
  interviewTitle,
  onFinish,
  onInterviewIndex,
  isLastQuestion,
  questionCount,
  submitInProgress,
}) {
  const params = getParams();
  const [stream, setStream] = useState(null);
  const [inFlight, setInFlight] = useState(true);
  const [initDone, setInitDone] = useState(false);
  const [mode, setMode] = useState(false);
  const [isSendEnabled, setIsSendEnabled] = useState(true);
  const [firstName, setFirstName] = useState(firstName_ ? firstName_ : "John");
  const [panelist, setPanelist] = useState(
    panelist_ === "undefinedundefined" ? `${Date.now()}` : panelist_
  );

  const [permissions] = useState(getLocalStorage(Keys.Permission));
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [questionNumber, setQuestionNumber] = useState(1);
  const [questionTotal, setQuestionTotal] = useState(0);

  console.log("debug", id, panelist, firstName);
  const studyId = id ? id : params.id;

  const lsKey = `interview-${studyId}-${interviewTitle}-${panelist}`;

  useEffect(() => {
    if (!permissions.video) setMode("audio");
  }, []);

  const updateLocalStorage = (newObj) => {
    let ar = getLocalStorage(lsKey);
    if (!Array.isArray(ar)) ar = [];
    ar.push(newObj);
    setLocalStorage(lsKey, ar);
  };

  const handleMediaStop = async (
    audioBlob,
    audioContext,
    destination,
    muteMic,
    videoRecorder
  ) => {
    console.log(
      "audioUrl",
      audioBlob,
      audioContext,
      destination,
      videoRecorder
    );
    videoRecorder.pauseRecording();

    try {
      const response = await sendAudio(audioBlob, studyId, panelist, true);
      console.warn(response);

      if (response) {
        setTitle(
          response.question !== ""
            ? response.question
            : response.questionContinue
        );
        setText(response.text);
        setQuestionNumber(response.questionNumber);
        setQuestionTotal(response.questionTotal);
      }

      videoRecorder.resumeRecording();
      if (response && response.audioContinue) {
        const playContinue = new Promise((resolve, reject) => {
          const audioData = atob(response.audioContinue);
          const audioArrayBuffer = new Uint8Array(audioData.length);
          for (let i = 0; i < audioData.length; i++)
            audioArrayBuffer[i] = audioData.charCodeAt(i);

          audioContext
            .decodeAudioData(audioArrayBuffer.buffer)
            .then((buffer) => {
              const playbackSource = audioContext.createBufferSource();
              playbackSource.buffer = buffer;
              playbackSource.connect(destination);
              playbackSource.connect(audioContext.destination);
              playbackSource.onended = () => {
                if (!response.audio) muteMic = false;
                resolve();
              };
              setInFlight(false);
              playbackSource.start();
            })
            .catch((error) => {
              console.error("Audio decoding error:", error);
              reject();
            });
        });
        await playContinue;
      }

      if (response && response.audio) {
        await new Promise((resolve) => videoRecorder.stopRecording(resolve));
        const videoBlob = videoRecorder.getBlob();
        videoRecorder.startRecording();

        //studyId, panelist,firstName,interviewTitle
        const videoKey = `${studyId}/${interviewTitle}_${Date.now()}_${
          response.questionContinue
        }-${panelist}.mp4`;
        if (id) {
          Storage.put(videoKey, videoBlob, { contentType: "video/mp4" })
            .then((s3Result) => {
              console.log("Video uploaded to S3:", s3Result);
              updateLocalStorage({
                question: response.questionContinue,
                s3key: videoKey,
              });
            })
            .catch((error) => {
              console.error("Error uploading video to S3, retrying:", error);
              Storage.put(videoKey, videoBlob, { contentType: "video/mp4" })
                .then((s3Result) => {
                  console.log("Video uploaded to S3:", s3Result);
                  updateLocalStorage({
                    question: response.questionContinue,
                    s3key: videoKey,
                  });
                })
                .catch((error) =>
                  console.error("Error uploading video to S3", error)
                );
            });
        } else {
          updateLocalStorage({
            question: response.questionContinue,
            s3key: videoKey,
          });
        }
        onInterviewIndex(response.questionNumber);

        const audioData = atob(response.audio);
        const audioArrayBuffer = new Uint8Array(audioData.length);
        for (let i = 0; i < audioData.length; i++) {
          audioArrayBuffer[i] = audioData.charCodeAt(i);
        }
        audioContext
          .decodeAudioData(audioArrayBuffer.buffer)
          .then((buffer) => {
            const playbackSource = audioContext.createBufferSource();
            playbackSource.buffer = buffer;
            playbackSource.connect(destination);
            playbackSource.connect(audioContext.destination);
            playbackSource.onended = () => {
              muteMic = false;
              if (response.question === "Closing") {
                if (isLastQuestion) {
                  submitInProgress(true);
                  setIsSendEnabled(false);
                  waitForLocalStorageItem(Max_TimeOut, () => {
                    submitInProgress(false);
                    onFinish(getLocalStorage(lsKey));
                    removeItem(lsKey);
                  });
                } else {
                  onFinish(getLocalStorage(lsKey));
                  removeItem(lsKey);
                }
              }
            };
            setInFlight(false);
            playbackSource.start();
          })
          .catch((error) => {
            console.error("Audio decoding error:", error);
          });
      }
    } catch (error) {
      console.error(error);
      Modal.error({
        title: error.response.data,
        content: Tech_Error_Message,
        onOk() {
          window.location.reload();
        },
      });
    }
  };

  function waitForLocalStorageItem(timeout, callback) {
    const checkInterval = 500;

    const intervalId = setInterval(() => {
      const item = getLocalStorage(lsKey);
      console.log(intervalId);
      if (Array.isArray(item) && item.length === questionCount) {
        clearInterval(intervalId);
        callback();
      }
    }, checkInterval);

    setTimeout(() => clearInterval(intervalId), timeout);
  }

  const handleMediaStart = async (audioUrl, videoUrl) => {
    console.log("onStart");
    setInFlight(true);
  };

  return (
    <>
      <InitVirtualInterviewer
        studyId={studyId}
        panelist={panelist}
        firstName={firstName}
        interviewTitle={interviewTitle}
        testMode={false}
        onDone={setInitDone}
      />
      {!mode && (
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            color="success"
            sx={buttonStyle}
            startIcon={<VolumeUpIcon style={{ fontSize: 20 }} />}
            onClick={() => setMode("audio")}
            disabled={!initDone}
            size="small"
          >
            <Typography sx={AnswerText}>Audio only</Typography>
          </Button>
          {permissions.video && permissions.audio && (
            <Button
              variant="outlined"
              color="success"
              sx={buttonStyle}
              startIcon={<VideocamIcon style={{ fontSize: 20 }} />}
              onClick={() => setMode("video")}
              disabled={!initDone}
              size="small"
            >
              <Typography sx={AnswerText}>Audio and Video</Typography>
            </Button>
          )}
        </Grid>
      )}

      {mode && (
        <RecordMedia
          onStart={handleMediaStart}
          onStop={handleMediaStop}
          isEnabled={isSendEnabled}
          isLoading={inFlight}
          mode={mode}
          data={{
            title,
            text,
            questionCount,
            questionNumber,
            questionTotal,
          }}
        />
      )}
    </>
  );
}

export default VideoChat;
